import { Component, NgZone, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { UserRoles } from 'src/app/dictionaries/UserRoles';
import { LocalStorageService } from '../../services/local-storage.service';
import { RedirectionService } from '../../services/redirection.service';
import { SimpleMessageWindowComponent } from '../ui/simple-message-window/simple-message-window.component';

import { AuthService } from '../../services/auth.service';
import { DialogService } from '../../services/dialog.service';
import { SessionStorageService } from '../../services/session-storage.service';
import { AllusersComponent } from '../allusers/allusers.component';
import { SearchComponent } from '../search/search.component';
import { FacilitiesListComponent } from '../facilities-list/facilities-list.component';

import { MatDialogConfig } from '@angular/material/dialog';
import { UserPlans } from 'src/app/dictionaries/UserPlans';
import { LibraryCategoriesComponent } from 'src/app/library-categories/library-categories.component';
import { FirebaseUtilitiesService } from 'src/app/services/firebase-utilities.service';
import { SafeLoggerService } from '../../services/safe-logger.service';
import { ContentHandlerService } from '../../services/content-handler.service';
import { AddLibraryWebSourceComponent } from '../add-library-web-source/add-library-web-source.component';
import { AttorneysLawFirmsComponent } from '../attorneys-law-firms/attorneys-law-firms.component';
import { CreatepatientComponent } from '../createpatient/createpatient.component';
import { CreateuserComponent } from '../createuser/createuser.component';
import { FeedLibraryComponent } from '../feed-library/feed-library.component';
import { OldPatientOverviewComponent } from '../old-patient-overview/old-patient-overview.component';
import { SpokesSearchComponent } from '../spokes-search/spokes-search.component';

import firebase from 'firebase/app';
const DEFAULT_PLAN = 'fp';
const dbPatients = firebase.firestore().collection('patients');
@Component({
    templateUrl: './home-page.component.html',
    styleUrls: ['./home-page.component.scss'],
    standalone: false
})
export class HomePageComponent implements OnInit, OnDestroy {
  userData: {};
  userEnabled: boolean;
  loaded: boolean;
  userRole: any;
  homepageParagraph: string;
  private routerSubscription?: Subscription;

  constructor(
    private dialog_$: DialogService,
    private redirection_$: RedirectionService,
    private localStorage_$: LocalStorageService,
    private sessionStorage_$: SessionStorageService,
    private auth_$: AuthService,
    public ngZone: NgZone,
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private contentHandler_$: ContentHandlerService,
    private firebaseUtilities_$: FirebaseUtilitiesService,
    private safeLogger: SafeLoggerService
  ) {
    this.loaded = false;
    this.homepageParagraph = this.contentHandler_$.homePageComponent('homePageParagraph');

    // Early check: if we're on a patient-overview route, don't initialize
    // This prevents HomePageComponent from interfering with route-specific components
    const currentUrl = this.router.url || window.location.pathname;
    if (currentUrl.startsWith('/patient-overview') && !currentUrl.startsWith('/patient-overview-modal')) {
      this.safeLogger.log('HomePageComponent: Constructor detected patient-overview route, skipping initialization');
      // Set loaded to true to prevent any further initialization
      this.loaded = true;
    }
  }

  ngOnInit(): void {
    // Check if we're on a patient-overview-modal route and handle it
    this.checkAndHandlePatientOverviewRoute();

    // Check if we're on an image-exchange route and handle it
    this.checkAndHandleImageExchangeRoute();

    // Also listen to router navigation events in case we navigate to the route after component init
    this.routerSubscription = this.router.events.pipe(filter(event => event instanceof NavigationEnd)).subscribe(() => {
      this.checkAndHandlePatientOverviewRoute();
      this.checkAndHandleImageExchangeRoute();
    });

    // Early exit if we're on a route handled by a specific component or if already marked as loaded
    const currentUrl = this.router.url;

    // Security check: If URL starts with /patient-overview/ (but not /patient-overview-modal),
    // ensure user is authenticated. This protects routes that fall through to catch-all.
    if (currentUrl.startsWith('/patient-overview/') && !currentUrl.startsWith('/patient-overview-modal')) {
      const isSignedIn = this.auth_$.userIsSignedIn.getValue();
      if (!isSignedIn) {
        this.safeLogger.log('HomePageComponent: Patient-overview route detected but user not authenticated, redirecting to login');
        this.router.navigate(['/login'], {
          queryParams: { referer: currentUrl }
        });
        return;
      }
      this.safeLogger.log('HomePageComponent: Skipping ngOnInit for patient-overview route');
      return; // This route is handled by PatientOverviewV2Component via routing
    }

    // Skip handling /patient-profile routes - component opens as dialog, route is just for URL
    if (currentUrl.startsWith('/patient-profile/')) {
      this.safeLogger.log('HomePageComponent: Skipping ngOnInit for patient-profile route (component opens as dialog)');
      this.loaded = true; // Mark as loaded to prevent any further processing
      return; // This route is just for URL, component opens as dialog
    }

    // Skip handling /image-exchange routes - handled by checkAndHandleImageExchangeRoute
    if (currentUrl.startsWith('/image-exchange/')) {
      this.safeLogger.log('HomePageComponent: Skipping ngOnInit for image-exchange route (handled separately)');
      this.loaded = true; // Mark as loaded to prevent any further processing
      return; // This route is handled by checkAndHandleImageExchangeRoute
    }

    if (this.loaded) {
      this.safeLogger.log('HomePageComponent: Already loaded, skipping ngOnInit');
      return;
    }

    // Fast path: user is already authenticated (in-app navigation).
    // onAuthStateChanged fires asynchronously and its timing relative to
    // ActivatedRoute snapshot updates is not guaranteed — skip it when we
    // already have everything we need.
    if (this.auth_$.userIsSignedIn.getValue() && Object.keys(this.auth_$.userData.getValue()).length > 0) {
      this.yesUserAction();
      return;
    }

    this.auth_$.auth.onAuthStateChanged({
      next: async user => {
        if (user) {
          if (Object.keys(this.auth_$.userData.getValue()).length > 0) {
            await this.yesUserAction();
          }
        } else {
          this.noUserAction();
        }
      },
      error: err => this.safeLogger.error('HomePageComponent: init subscription error', err),
      complete: () => this.safeLogger.log('HomePageComponent: init subscription complete')
    });
  }

  /**
   * Handle Keycloak authentication errors from URL hash or query string
   * Keycloak returns errors like: #error=login_required&state=...&iss=... or ?error=login_required&state=...
   */
  private handleKeycloakAuthError(): void {
    // Check both hash and query string for errors
    const hash = window.location.hash;
    const search = window.location.search;

    // If there's a callback code in the URL, don't treat login_required as an error yet
    // The callback might still be processing
    const hasCallbackCode = (hash && hash.includes('code=')) || (search && search.includes('code='));
    if (hasCallbackCode) {
      this.safeLogger.log('[HomePage] Callback code detected, ignoring login_required error (callback still processing)');
      return;
    }

    let params: URLSearchParams;
    let error: string | null = null;
    let state: string | null = null;

    // Check hash first
    if (hash) {
      params = new URLSearchParams(hash.substring(1));
      error = params.get('error');
      state = params.get('state');
    }

    // If not in hash, check query string
    if (!error && search) {
      params = new URLSearchParams(search.substring(1));
      error = params.get('error');
      state = params.get('state');
    }

    if (!error) return;

    if (error === 'login_required') {
      this.safeLogger.log('[HomePage] Keycloak login_required error detected, redirecting to login');

      // Store return URL before redirecting (check orthanc_return_url first)
      let returnUrl = sessionStorage.getItem('orthanc_return_url');
      if (!returnUrl) {
        const storedRoute = this.sessionStorage_$.getAndClearReturnUrl();
        if (storedRoute) {
          returnUrl = storedRoute;
          this.safeLogger.log('[HomePage] Using stored route as return URL');
        } else {
          // Use current URL as fallback
          returnUrl = window.location.pathname + window.location.search;
          // Remove error parameters from URL
          if (returnUrl.includes('#')) {
            returnUrl = returnUrl.split('#')[0];
          }
        }
        // Store it for the callback handler
        sessionStorage.setItem('orthanc_return_url', returnUrl);
        this.safeLogger.log('[HomePage] Stored return URL');
      } else {
        this.safeLogger.log('[HomePage] Return URL already stored');
      }

      // Clear the error from URL immediately
      window.history.replaceState(null, '', window.location.pathname);

      // Check if we've already tried to redirect (prevent infinite loop)
      const redirectAttempted = sessionStorage.getItem('keycloak_redirect_attempted');
      const redirectTime = redirectAttempted ? parseInt(redirectAttempted, 10) : 0;
      const now = Date.now();

      // If we tried to redirect in the last 2 seconds, don't try again
      if (redirectAttempted && now - redirectTime < 2000) {
        this.safeLogger.log('[HomePage] Redirect attempted recently, waiting...');
        return;
      }

      // Mark that we're attempting a redirect with timestamp
      sessionStorage.setItem('keycloak_redirect_attempted', now.toString());

      // Get Keycloak config from environment
      const keycloakConfig = {
        url: 'http://localhost:8080',
        realm: 'Orthanc_PACS_Realm',
        clientId: 'orthanc-client'
      };

      // Construct login URL directly with prompt=login to force login screen
      const redirectUri = encodeURIComponent(window.location.origin + '/');
      const loginUrl =
        `${keycloakConfig.url}/realms/${keycloakConfig.realm}/protocol/openid-connect/auth?` +
        `client_id=${keycloakConfig.clientId}&` +
        `redirect_uri=${redirectUri}&` +
        `response_type=code&` +
        `scope=openid&` +
        `prompt=login`; // Force login screen

      this.safeLogger.log('[HomePage] Redirecting directly to Keycloak login');

      // Redirect directly to avoid another silent check
      window.location.href = loginUrl;
    }
  }

  private checkAndHandlePatientOverviewRoute(): void {
    const currentUrl = this.router.url;
    this.safeLogger.log('HomePageComponent: checkAndHandlePatientOverviewRoute');

    if (currentUrl.startsWith('/patient-overview-modal/')) {
      this.safeLogger.log('HomePageComponent: Detected patient-overview-modal route');

      this.sessionStorage_$.setReturnUrl(currentUrl);
      this.safeLogger.log('HomePageComponent: Stored patient overview route');
      // Wait for auth state before loading patient data
      this.auth_$.auth.onAuthStateChanged({
        next: async user => {
          this.safeLogger.log('HomePageComponent: Auth state changed, user:', user ? 'authenticated' : 'not authenticated');
          if (user) {
            // Wait a bit for userData to be populated
            let attempts = 0;
            const maxAttempts = 20; // Increased attempts
            while (attempts < maxAttempts) {
              const userData = this.auth_$.userData.getValue();
              if (userData && Object.keys(userData).length > 0) {
                this.safeLogger.log('HomePageComponent: UserData ready, loading patient overview modal');
                await this.handlePatientOverviewModalRoute();
                return;
              }
              await new Promise(resolve => setTimeout(resolve, 200));
              attempts++;
            }
            this.safeLogger.warn('HomePageComponent: UserData not ready after waiting');
          }
        },
        error: err => this.safeLogger.error('HomePageComponent: Auth error', err),
        complete: () => this.safeLogger.log('HomePageComponent: Auth state change complete')
      });
    }
  }

  /**
   * Handle image-exchange routes by loading patient data and opening the dialog
   * URL pattern: /image-exchange/:patientId/from/:senderFacility/to/:targetFacility/images/retreive
   */
  private checkAndHandleImageExchangeRoute(): void {
    const currentUrl = this.router.url;
    this.safeLogger.log('HomePageComponent: checkAndHandleImageExchangeRoute');

    if (currentUrl.startsWith('/image-exchange/')) {
      this.safeLogger.log('HomePageComponent: Detected image-exchange route');

      this.sessionStorage_$.setReturnUrl(currentUrl);
      this.safeLogger.log('HomePageComponent: Stored image-exchange route');

      // Wait for auth state before loading patient data
      this.auth_$.auth.onAuthStateChanged({
        next: async user => {
          this.safeLogger.log('HomePageComponent: Auth state changed for image-exchange, user:', user ? 'authenticated' : 'not authenticated');
          if (user) {
            // Wait a bit for userData to be populated
            let attempts = 0;
            const maxAttempts = 20;
            while (attempts < maxAttempts) {
              const userData = this.auth_$.userData.getValue();
              if (userData && Object.keys(userData).length > 0) {
                this.safeLogger.log('HomePageComponent: UserData ready, loading patient overview for image-exchange');
                await this.handleImageExchangeRoute();
                return;
              }
              await new Promise(resolve => setTimeout(resolve, 200));
              attempts++;
            }
            this.safeLogger.warn('HomePageComponent: UserData not ready after waiting for image-exchange route');
          }
        },
        error: err => this.safeLogger.error('HomePageComponent: Auth error for image-exchange', err),
        complete: () => this.safeLogger.log('HomePageComponent: Auth state change complete for image-exchange')
      });
    }
  }

  async getPatientOverviewData(caseName: string) {
    this.safeLogger.log('getPatientOverviewData: loading patient data');
    try {
      // First, try to get the document by ID (caseName)
      let doc = await dbPatients.doc(caseName).get();

      // If document doesn't exist by ID, try querying by caseName field
      if (!doc.exists) {
        this.safeLogger.log('getPatientOverviewData: Document not found by ID, querying by caseName field');
        const querySnapshot = await dbPatients.where('caseName', '==', caseName).limit(1).get();

        if (!querySnapshot.empty) {
          doc = querySnapshot.docs[0];
          this.safeLogger.log('getPatientOverviewData: Found document by caseName field');
        } else {
          // Try lowercase version
          const lowerCaseQuery = await dbPatients.where('caseName', '==', caseName.toLowerCase()).limit(1).get();
          if (!lowerCaseQuery.empty) {
            doc = lowerCaseQuery.docs[0];
            this.safeLogger.log('getPatientOverviewData: Found document by lowercase caseName');
          }
        }
      }

      if (!doc.exists) {
        this.safeLogger.warn('getPatientOverviewData: Patient document does not exist');
        return null;
      }

      const data = doc.data();
      if (!data) {
        this.safeLogger.warn('getPatientOverviewData: Patient document has no data');
        return null;
      }

      const finalData: any = {
        ...data,
        firstname: data.FirstName,
        lastname: data.LastName,
        dob: data.DateOfBirth,
        patientIDs: data.metadata?.medical_record_numbers?.map((item: any) => ({
          facility: item.facility,
          value: item.value
        })) || [],
        placeOfBirth: data.metadata?.placeOfBirth,
        spouseName: data.metadata?.spouseName
      };

      return finalData;
    } catch (error) {
      this.safeLogger.error('getPatientOverviewData: Error loading patient data', error);
      throw error;
    }
  }

  /**
   * Handle image-exchange route by loading patient data and opening the dialog
   */
  private async handleImageExchangeRoute(): Promise<void> {
    const currentUrl = this.router.url;
    this.safeLogger.log('HomePageComponent: handleImageExchangeRoute');

    // Extract patientId from URL pattern: /image-exchange/:patientId/from/:senderFacility/to/:targetFacility/images/retreive
    const urlMatch = currentUrl.match(/\/image-exchange\/([^\/]+)/);

    if (!urlMatch || !urlMatch[1]) {
      this.safeLogger.warn('HomePageComponent: Invalid image-exchange route, missing patientId');
      return;
    }

    const caseName = urlMatch[1];
    this.safeLogger.log('HomePageComponent: Extracted caseName from image-exchange route');

    const userData = this.auth_$.userData.getValue();
    this.safeLogger.log('HomePageComponent: UserData:', userData ? 'available' : 'not available');

    if (!userData || Object.keys(userData).length === 0) {
      this.safeLogger.warn('HomePageComponent: User not authenticated, cannot load patient data');
      return;
    }

    try {
      this.safeLogger.log('HomePageComponent: Loading patient for image-exchange');

      // FIXME: Find a better way to get the patientData
      const patientData = await this.getPatientOverviewData(caseName);

      if (!patientData) {
        this.safeLogger.error('HomePageComponent: Patient not found for image-exchange');
        return;
      }

      this.safeLogger.log('HomePageComponent: Patient data loaded for image-exchange');

      // Transform patient data similar to SearchComponent.getPatientObject
      const patient = this.transformPatientData(patientData);
      this.safeLogger.log('HomePageComponent: Patient transformed, opening dialog');

      this.openPatientOverviewDialog(patient);
    } catch (error) {
      this.safeLogger.error('HomePageComponent: Error loading patient data for image-exchange', error);
      this.safeLogger.error('HomePageComponent: Error details', error);
    }
  }

  ngOnDestroy(): void {
    if (this.routerSubscription) {
      this.routerSubscription.unsubscribe();
    }
  }

  /**
   * Handle patient-overview-modal routes by loading patient data and opening the dialog
   */
  private async handlePatientOverviewModalRoute(): Promise<void> {
    const currentUrl = this.router.url;
    this.safeLogger.log('HomePageComponent: handlePatientOverviewModalRoute, currentUrl:', currentUrl);

    const urlMatch = currentUrl.match(/\/patient-overview-modal\/([^\/]+)/);

    if (!urlMatch || !urlMatch[1]) {
      this.safeLogger.warn('HomePageComponent: Invalid patient-overview-modal route');
      return;
    }

    const caseName = urlMatch[1];
    this.safeLogger.log('HomePageComponent: Extracted caseName:', caseName);

    const userData = this.auth_$.userData.getValue();
    this.safeLogger.log('HomePageComponent: UserData:', userData ? 'available' : 'not available');

    if (!userData || Object.keys(userData).length === 0) {
      this.safeLogger.warn('HomePageComponent: User not authenticated, cannot load patient data');
      return;
    }

    try {
      this.safeLogger.log('HomePageComponent: Loading patient with caseName:', caseName);

      // OPTION 2: No early initialization needed - tokens will be used directly in getOrthancUrl()
      // The facade will check for tokens and skip pre-auth if they exist
      // If tokens don't exist, the facade will handle authentication

      // Use ClientMatterService which has better patient lookup logic
      const patientData = await this.getPatientOverviewData(caseName);

      if (!patientData) {
        this.safeLogger.error('HomePageComponent: Patient not found for caseName:', caseName);
        return;
      }

      this.safeLogger.log('HomePageComponent: Patient data loaded:', patientData?.caseName);

      // Transform patient data similar to SearchComponent.getPatientObject
      const patient = this.transformPatientData(patientData);
      this.safeLogger.log('HomePageComponent: Patient transformed, opening dialog');

      this.openPatientOverviewDialog(patient);
    } catch (error) {
      this.safeLogger.error('HomePageComponent: Error loading patient data', error);
      this.safeLogger.error('HomePageComponent: Error details', error);
    }
  }

  /**
   * Open the patient overview dialog
   */
  private openPatientOverviewDialog(patient: any): void {
    try {
      this.safeLogger.log('HomePageComponent: Opening dialog with patient:', patient?.caseName);

      // Check if dialog is already open to prevent duplicates
      const openDialogs = this.dialog_$.getOpenDialogs();
      const isAlreadyOpen = openDialogs.some(dialog => {
        const componentInstance = dialog.componentInstance;
        return componentInstance instanceof OldPatientOverviewComponent;
      });

      if (isAlreadyOpen) {
        this.safeLogger.log('HomePageComponent: Dialog already open, skipping duplicate open');
        return;
      }

      this.sessionStorage_$.clearReturnUrl();

      // Open the dialog
      const dialogConfig = new MatDialogConfig();
      dialogConfig.autoFocus = false;
      dialogConfig.width = '95vw';
      dialogConfig.panelClass = 'patient-overview-dialog';
      dialogConfig.data = { patient };

      this.safeLogger.log('HomePageComponent: Dialog config created, opening...');
      const patientDialogRef = this.dialog_$.dialog.open(OldPatientOverviewComponent, dialogConfig);
      this.safeLogger.log('HomePageComponent: Dialog opened successfully, dialogRef:', patientDialogRef);

      // Check if we should restore fullscreen mode (e.g., after Keycloak redirect)
      // If the route includes /retreive, we need to go to fullscreen mode
      const currentUrl = this.router.url;
      if (currentUrl.includes('/retreive')) {
        this.safeLogger.log('[HomePage] Route includes /retreive, will restore fullscreen mode after dialog initializes');

        // Wait for dialog to initialize, then trigger fullscreen mode
        // Use a retry mechanism to ensure the facade is ready
        let retries = 0;
        const maxRetries = 10;
        const retryDelay = 200; // 200ms between retries

        const tryRestoreFullscreen = async () => {
          try {
            const componentInstance = patientDialogRef.componentInstance as OldPatientOverviewComponent;
            if (componentInstance && (componentInstance as any).facade) {
              const facade = (componentInstance as any).facade;

              // Ensure share type is set
              if (!facade.shareTypeSelected) {
                facade.onShareTypeSelected('image');
              }

              // OPTION 2: Call onReceiveSelected() which will pre-authenticate and go to fullscreen
              // This ensures authentication happens before fullscreen, preventing redirects during fullscreen
              // If user is already authenticated (after Keycloak redirect), this will be quick
              // If not authenticated, it will redirect to login, then return and call this again
              await facade.onReceiveSelected();

              this.safeLogger.log('[HomePage] Fullscreen mode restored after Keycloak redirect');
              return true; // Success
            } else if (retries < maxRetries) {
              // Facade not ready yet, retry
              retries++;
              setTimeout(tryRestoreFullscreen, retryDelay);
              return false; // Not ready yet
            } else {
              this.safeLogger.warn(
                '[HomePage] Could not restore fullscreen mode: facade not available after',
                maxRetries,
                'retries'
              );
              return false;
            }
          } catch (error) {
            if (retries < maxRetries) {
              retries++;
              setTimeout(tryRestoreFullscreen, retryDelay);
              return false;
            } else {
              this.safeLogger.warn('[HomePage] Could not restore fullscreen mode', error);
              return false;
            }
          }
        };

        // Start trying to restore fullscreen after initial delay
        setTimeout(tryRestoreFullscreen, 300);
      }

      // When dialog closes, navigate back if still on patient-overview-modal route
      patientDialogRef.afterClosed().subscribe(() => {
        this.safeLogger.log('HomePageComponent: Dialog closed');
        if (this.router.url.includes('/patient-overview-modal/')) {
          this.router.navigateByUrl('/');
        }
      });
    } catch (error) {
      this.safeLogger.error('HomePageComponent: Error opening dialog', error);
      this.safeLogger.error('HomePageComponent: Dialog error details:', error.message, error.stack);
    }
  }

  /**
   * Transform patient data from Firestore to the format expected by OldPatientOverviewComponent
   */
  private transformPatientData(patient: any): any {
    const userData = this.auth_$.userData.getValue();
    const firstName = patient.FirstName || patient?.metadata?.firstName || '';
    const lastName = patient.LastName || patient?.metadata?.lastName || '';
    const dobSource = patient.DateOfBirth || patient?.metadata?.dateOfBirth || patient?.metadata?.dob || '';

    return {
      patientId: patient.caseName || '',
      caseName: patient.caseName || '',
      firstname: firstName,
      lastname: lastName,
      clientname: patient.clientname || '',
      gender: patient.metadata?.gender || patient.Gender || '',
      dob: this.formatDate(dobSource),
      placeOfBirth: patient.metadata?.placeOfBirth || '',
      patientPicture: patient.patientPicture || '',
      ownerID: patient.ownerID || userData['ownerID'] || userData['uid'],
      owners: patient.ownerID ? [patient.ownerID] : [],
      type: patient.type || '',
      LegalCaseId: patient.LegalCaseId || '',
      files: patient.files || [],
      extraMessage: patient.extraMessage || '',
      defaultFolders: patient.defaultFolders || [],
      metadata: patient.metadata || {},
      patientIDs: this.normalizePatientIDs(patient?.metadata?.medical_record_numbers, patient.caseName)
    };
  }

  /**
   * Normalize patient IDs from metadata
   */
  private normalizePatientIDs(
    medicalRecordNumbers: any[],
    caseName?: string
  ): Array<{ value: string; facility: string; current?: boolean }> {
    if (!Array.isArray(medicalRecordNumbers)) {
      return [];
    }

    return medicalRecordNumbers.map(record => {
      let value: string, facility: string;

      if (typeof record === 'object' && record !== null) {
        value = String(record.value || record.patientId || record.id || '');
        facility = String(record.facility || record.facilityId || '');
      } else {
        value = String(record || '');
        facility = '';
      }

      const result: { value: string; facility: string; current?: boolean } = { value, facility };
      if (caseName && value === caseName) {
        result.current = true;
      }

      return result;
    });
  }

  /**
   * Format date string
   */
  private formatDate(dateSource: any): string {
    if (!dateSource) return '';

    if (dateSource instanceof Date) {
      return dateSource.toLocaleDateString();
    }

    if (typeof dateSource === 'string' || typeof dateSource === 'number') {
      const date = new Date(dateSource);
      if (!isNaN(date.getTime())) {
        return date.toLocaleDateString();
      }
    }

    return String(dateSource);
  }

  getOwnerPlanCode(ownerID: string) {
    return this.auth_$.getOwnerPlanCode(ownerID);
  }

  private buildOptions = (data: any = null, role?: string, id?: string, extraOptions: object = {}) => {
    const options = { width: '600px', ...(id && { id }), data: data ?? (role ? { role } : undefined), ...extraOptions };
    return options;
  };

  async handleRoute(): Promise<boolean> {
    if (!this.activatedRoute.snapshot.url[0]) {
      return false;
    }

    // Skip handling routes that are handled by specific route components
    const firstPathSegment = this.activatedRoute.snapshot.url[0].path;
    if (firstPathSegment === 'patient-overview') {
      return false; // This route is handled by PatientOverviewV2Component via routing
    }
    if (firstPathSegment === 'patient-profile') {
      return false; // This route is handled by ClientProfileComponent via routing
    }
    if (firstPathSegment === 'image-exchange') {
      return false; // This route is handled by checkAndHandleImageExchangeRoute
    }

    let role: string;
    let data: any;
    let rolesAllowed: string | any[];
    let component: any = AllusersComponent;
    let id = null;
    let options = null;

    switch (firstPathSegment) {
      case 'consultants':
        rolesAllowed = [UserRoles.admin, UserRoles.owner, UserRoles.associate];
        role = UserRoles.consultant;
        break;
      case 'facilities':
        rolesAllowed = [UserRoles.admin, UserRoles.owner, UserRoles.associate];
        role = UserRoles.facility;
        break;
      case 'facilities-list':
        rolesAllowed = [UserRoles.admin, UserRoles.owner, UserRoles.associate, UserRoles.superuser];
        component = FacilitiesListComponent;
        id = 'facilities-list';
        data = null;
        options = this.buildOptions(data, role, id, { width: '600px', maxWidth: '800px' });
        break;
      case 'owners':
        rolesAllowed = [UserRoles.superuser];
        role = UserRoles.owner;
        break;
      case 'associates':
        rolesAllowed = [UserRoles.admin, UserRoles.owner, UserRoles.associate];
        role = UserRoles.associate;
        break;
      case 'admins':
        rolesAllowed = [UserRoles.owner, UserRoles.admin];
        role = UserRoles.admin;
        break;
      case 'attorneys_lawfirms':
        rolesAllowed = [UserRoles.consultant];
        component = AttorneysLawFirmsComponent;
        break;
      case 'feedlibrary':
        rolesAllowed = [UserRoles.owner, UserRoles.admin, UserRoles.associate, UserRoles.consultant];
        component = FeedLibraryComponent;
        id = 'feed-library';
        data = { role: this.auth_$.userData.value['role'] };
        options = this.buildOptions(data, role, id, { height: '60vh', width: '90vw', top: '5%' });
        break;
      case 'librarycategories':
        rolesAllowed = [UserRoles.owner, UserRoles.admin, UserRoles.associate, UserRoles.consultant];
        component = LibraryCategoriesComponent;
        id = 'library-categories';
        data = { role: this.auth_$.userData.value['role'], dynamic: true };
        options = this.buildOptions(data, role, id, { height: '60vh', width: '90vw' });
        break;
      case 'clients':
        rolesAllowed = [UserRoles.admin, UserRoles.owner, UserRoles.associate];
        role = UserRoles.client;
        id = 'clients';
        break;
      case 'createclientmatter':
        rolesAllowed = [UserRoles.admin, UserRoles.owner, UserRoles.associate, UserRoles.consultant];
        role = UserRoles.client;
        component = CreatepatientComponent;
        data = { facilities: await this.firebaseUtilities_$.getFacilities() };
        id = 'createclientmatter';
        break;
      case 'createclientsmatters':
        // Header opens this dialog directly — skip if already open to avoid a duplicate
        if (this.dialog_$.getOpenDialogs().some(d => d.componentInstance instanceof CreatepatientComponent)) {
          return true;
        }
        rolesAllowed = [UserRoles.admin, UserRoles.owner, UserRoles.associate, UserRoles.consultant];
        role = this.auth_$.userData.value['role'];
        component = CreatepatientComponent;
        id = 'create-patient';
        data = { facilities: await this.firebaseUtilities_$.getFacilities() };
        options = this.buildOptions(data, role, id, { width: '550px' });
        break;
      // Note: 'patient-overview' route is handled by a specific route in app-routing.module.ts
      // and should not be handled here to avoid conflicts
      case 'addsources':
        rolesAllowed = [UserRoles.admin, UserRoles.owner, UserRoles.associate, UserRoles.consultant];
        role = this.auth_$.userData.value['role'];
        component = AddLibraryWebSourceComponent;
        id = 'add-library-web-source';
        data = null;
        break;
      case 'createadminuser':
        rolesAllowed = [UserRoles.admin, UserRoles.owner, UserRoles.associate, UserRoles.consultant, UserRoles.client];
        role = this.auth_$.userData.value['role'];
        component = CreateuserComponent;
        id = 'create-admin-user';
        data = {
          userType: 'Admin',
          title: 'Create a user',
          button: 'Create',
          screen: 'full',
          limits: { limits: await this.checkPlanLimits() }
        };
        break;
      case 'lpmwelcome':
        rolesAllowed = [UserRoles.owner];
        options = this.buildOptions(
          {
            title: 'Welcome to Nuage Diagnostics',
            message: `We are excited to have you on board. Please feel free to reach out to us at any time.`,
            id: 'lpm-welcome'
          },
          null,
          id
        );
        this.dialog_$
          .open(SimpleMessageWindowComponent, options)
          .afterClosed()
          .subscribe(() => this.goHome());
        return;
      case 'comingsoon':
        rolesAllowed = [UserRoles.owner];
        component = SimpleMessageWindowComponent;
        id = 'coming-soon';
        data = {
          title: 'Coming Soon',
          message: 'This feature is not available yet. Please check back soon.',
          id: 'coming-soon'
        };

        this.safeLogger.log('this.activatedRoute.snapshot.queryParams.type: ', this.activatedRoute.snapshot.queryParams.type);

        switch (this.activatedRoute.snapshot.queryParams.type) {
          case 'plan-upgrade':
            data.message = `<ul>
          <li>Employee medical records</li>
          <li>Family Video conference</li>
          <li>Patient Educational Library</li>
          <li>Patient/Family comunications and information sharing</li>
          </ul>`;
            data.html = true;
            break;
          case 'video-conferencing':
            data.message = 'This feature is not available yet. Please check back soon.';
            data.html = false;
            break;
        }

        options = this.buildOptions(data, role, id);
        break;
      default:
        component = null;
        options = null;
        break;
    }

    if (rolesAllowed?.includes(this.auth_$.userData.value['role'])) {
      const dialogOpen = this.dialog_$.getOpenDialogs().length > 0 ? this.dialog_$.getOpenDialogs()[0] : null;
      if (dialogOpen) {
        dialogOpen.close();
      }

      // Component.
      this.dialog_$
        .open(component, options || this.buildOptions(data, role, id))
        .afterClosed()
        .subscribe(answer => {
          // If the dialog itself already handled navigation (e.g., search dialog navigated
          // to patient-overview), do NOT force a redirect to home.
          if (answer?.navigated) {
            return;
          }

          if (answer?.type === 'owner' || !answer?.client) {
            this.goHome();
            if (answer?.type === 'owner') {
              this.dialog_$.open(SearchComponent, { data: { ownerID: answer.value } });
            }
          }
        });
      return true;
    } else {
      const data = { title: 'Access Denied', message: `You do not have access to this page.` };
      const options = this.buildOptions(data, role, 'access-denied');

      this.dialog_$
        .open(SimpleMessageWindowComponent, options)
        .updatePosition({ top: '5%' })
        .afterClosed()
        .subscribe(() => this.goHome());
      return true;
    }
  }


  async yesUserAction() {
    // Early exit if we're on a route handled by a specific component
    // Check both ActivatedRoute and Router to be sure
    const currentUrl = this.router.url;
    const firstPathSegment = this.activatedRoute.snapshot.url[0]?.path;

    if (currentUrl.startsWith('/patient-overview') || firstPathSegment === 'patient-overview') {
      this.safeLogger.log('HomePageComponent: Skipping yesUserAction for patient-overview route', {
        currentUrl,
        firstPathSegment
      });
      return; // This route is handled by PatientOverviewV2Component via routing
    }

    if (currentUrl.startsWith('/patient-profile') || firstPathSegment === 'patient-profile') {
      this.safeLogger.log('HomePageComponent: Skipping yesUserAction for patient-profile route', {
        currentUrl,
        firstPathSegment
      });
      return; // This route is handled by ClientProfileComponent via routing
    }

    this.auth_$.hideLoader();
    this.userData = this.auth_$.userData.getValue();
    this.userEnabled = this.auth_$.userIsSignedIn.getValue();
    this.userRole = this.auth_$.userData.getValue()['role'];
    this.loaded = true;

    // Move paramMap subscription here
    this.activatedRoute.paramMap.subscribe(async params => {
      const windowType = params.get('type');
      const currentPathSegment = this.activatedRoute.snapshot.url[0]?.path; // Get the main path segment

      // Skip handling routes that are handled by specific route components
      if (currentPathSegment === 'patient-overview') {
        return; // This route is handled by PatientOverviewV2Component via routing
      }

      if (currentPathSegment === 'patient-profile') {
        return; // This route is handled by ClientProfileComponent via routing
      }

      if (currentPathSegment === 'clientsmatters' && windowType) {
        await this.handleClientsMattersRoute(windowType);
      } else {
        await this.handleRoute(); // Call original handleRoute if not clientsmatters with type
      }
    });

    this.fromClioAction();
  }

  /** Handles route when not from Clio; Clio-specific logic removed. */
  async notFromClioAction() {
    const firstPathSegment = this.activatedRoute.snapshot.url[0]?.path;
    if (firstPathSegment === 'patient-overview') return;
    if (firstPathSegment === 'patient-profile') return;

    const routeHandled = await this.handleRoute();
    if (!routeHandled) {
      this.noUserAction();
    }
  }

  /** @deprecated Clio integration removed; no-op. */
  fromClioAction(): void { }

  noUserAction() {
    this.safeLogger.log('noUserAction');
  }

  async checkPlanLimits() {
    const plancode = this.auth_$.userData.value['plancode'];
    const planlimits = this.getPlanLimits(plancode);
    const usersCount = this.getUsersCount();
    const [limits, users] = await Promise.all([planlimits, usersCount]);
    return { limits, users };
  }

  private getUsersCount() {
    return this.auth_$.getUsersCount();
  }

  private getPlanLimits(planCode: string) {
    return this.auth_$.getPlanLimits(planCode);
  }

  goHome() {
    this.router.navigate(['']);
  }

  // New method to handle 'clientsmatters' route with a 'type' parameter
  private async handleClientsMattersRoute(windowType: string): Promise<void> {
    this.safeLogger.log('HomePageComponent: handleClientsMattersRoute called', { windowType });
    // Don't open search component if we're on patient-profile route
    const currentUrl = this.router.url;
    if (currentUrl.startsWith('/patient-profile/')) {
      this.safeLogger.log('HomePageComponent: On patient-profile route, skipping search component');
      return;
    }

    // Don't open search component if client profile is already open
    const openDialogs = this.dialog_$.getOpenDialogs();
    const clientProfileDialog = openDialogs.some((d: any) => d.id === 'client-profile');
    if (clientProfileDialog) {
      this.safeLogger.log('HomePageComponent: Client profile is open, skipping search component');
      return;
    }

    const role = this.auth_$.userData.value['role'] ?? this.auth_$.userData.value['realRole'];
    const plan = this.auth_$.userData.value['plancode'];
    const component = plan === UserPlans.spoke ? SpokesSearchComponent : SearchComponent;
    const componentName = plan === UserPlans.spoke ? 'SpokesSearchComponent' : 'SearchComponent';
    this.safeLogger.log('HomePageComponent: Opening patient list', { plan, component: componentName, role, windowType });
    const id = 'search';
    const ownerID = await this.getOwnerId();
    if (!ownerID) {
      this.safeLogger.error(
        'HomePageComponent: Cannot open patient list - ownerID is missing. userData:',
        this.auth_$.userData.getValue()
      );
      this.dialog_$.open(SimpleMessageWindowComponent, {
        data: {
          title: 'Configuration Error',
          message: 'Your user account is missing required data. Please contact your administrator.',
          buttons: [{ text: 'OK', value: true }]
        }
      });
      return;
    }
    // Facility sub-users (CLIENT_ADMIN) cannot query their hub's user document directly —
    // Firestore rules cannot prove cross-document ownership without get() calls.
    // Fall back to the user's own plancode; ownerPlanCode is not used by SpokesSearchComponent.
    let ownerPlanCode: string | null = null;
    try {
      ownerPlanCode = await this.getOwnerPlanCode(ownerID);
    } catch (e) {
      ownerPlanCode = this.auth_$.userData.getValue()['plancode'] ?? null;
    }
    const data = { role, id, ownerID, ownerPlanCode, type: windowType, spoke: plan === UserPlans.spoke };
    this.safeLogger.log('HomePageComponent: Dialog data', { ownerID, patientType: windowType, role });

    const options = this.buildOptions(data, role, id, { height: 'auto', width: '450px' });

    // If the role is admin, owner, associate, or consultant, open the search component
    if ([UserRoles.admin, UserRoles.owner, UserRoles.associate, UserRoles.consultant].includes(role)) {
      this.dialog_$
        .open(component, options)
        .afterClosed()
        .subscribe(answer => {
          // Don't navigate home if the dialog closed with a navigation action
          if (answer?.navigated) {
            this.safeLogger.log('HomePageComponent: Dialog closed with navigation, skipping goHome()');
            return;
          }

          if (answer?.type === 'owner' || !answer?.client) {
            this.goHome();
            if (answer?.type === 'owner') {
              this.dialog_$.open(SearchComponent, { data: { ownerID: answer.value } });
            }
          }
        });
    } else {
      // If the role is not admin, owner, associate, or consultant, open the simple message window component
      this.dialog_$.open(SimpleMessageWindowComponent, {
        data: {
          title: 'Unauthorized',
          message: `You are not authorized to access this section.`,
          buttons: [{ text: 'OK', value: true }]
        }
      });
    }
  }

  // Assuming getOwnerId is a method that already exists or needs to be added
  // If it's private, it needs to be made accessible or its logic duplicated.
  // I've included a basic version below, adjust if your existing one is different.
  private async getOwnerId(): Promise<string> {
    const userData = this.auth_$.userData.getValue();
    const role = userData['role'] ?? userData['realRole'];
    const authUid = this.auth_$.currentUser?.getValue()?.uid;
    if (role === UserRoles.owner) {
      return userData['uid'] ?? userData['ownerID'] ?? authUid ?? '';
    }
    return userData['ownerID'] ?? userData['uid'] ?? authUid ?? '';
  }
}
